import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { formatAsCLP } from "src/utils/formatNumber";
import { formatearFechaDisplay } from "src/utils/formatTime";
import { useClienteActual } from "src/hooks/useClienteActual";
import {
  useObtenerFacturaEspecificaQuery,
  useRegistrarPagoWebPayMutation,
  useRegistrarPagoMultipleMutation,
} from "src/services/accesoClientesApi";
import {
  useCommitTransaccionMutation,
  useCrearTransaccionMutation,
} from "src/services/webpayApi";
import GetAppIcon from "@material-ui/icons/GetApp";
import { generarYDescargarComprobantePDF } from "src/utils/generarComprobantePDF";

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const FASE = {
  CARGANDO: "cargando",
  CREANDO: "creando",
  LISTO: "listo",
  PROCESANDO: "procesando",
  EXITO: "exito",
  ERROR: "error",
};

const TIPO_LABEL = { PLAN: "Plan mensual", TAREA: "Tarea adicional" };

// Medio de pago: solo débito / crédito / prepago
const MEDIO_PAGO = {
  VD: "Débito",
  VN: "Crédito",
  VC: "Crédito",
  SI: "Crédito",
  S2: "Crédito",
  NC: "Crédito",
  VP: "Prepago",
};

const formatearIdentidadComprobante = ({ nombre, rut } = {}) =>
  [nombre, rut ? `RUT ${rut}` : null].filter(Boolean).join(" · ");

const formatearFacturaComprobante = (factura) =>
  [
    `Folio ${factura.folio}`,
    formatearIdentidadComprobante({
      nombre: factura.nombreSociedad,
      rut: factura.rutSociedad,
    }),
  ]
    .filter(Boolean)
    .join(" - ");

// Modalidad de cuotas: derivada del código + installments_number
function describirCuotas(payment_type_code, installments_number) {
  switch (payment_type_code) {
    case "VD":
    case "VN":
    case "VP":
      return "Sin cuotas";
    case "VC":
      return `${installments_number} cuotas`;
    case "SI":
      return "3 cuotas sin interés";
    case "S2":
      return "2 cuotas sin interés";
    case "NC":
      return `${installments_number} cuotas sin interés`;
    default:
      return installments_number > 0
        ? `${installments_number} cuotas`
        : "Sin cuotas";
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convierte el sufijo numérico del param a los valores que necesitan los endpoints */
function parsearId(id) {
  const [idFactura, sufijo] = id.split("_");
  const tipoPago = sufijo === "0" ? "PLAN" : "TAREA";
  // El endpoint de facturas espera "planes" / "tareas"
  const tipoQuery = sufijo === "0" ? "planes" : "tareas";
  return { idFactura, tipoPago, tipoQuery };
}

/** Extrae los parámetros que Transbank puede devolver según el flujo */
function extraerParamsCommit(searchParams) {
  return {
    token_ws: searchParams.get("token_ws") || undefined,
    TBK_TOKEN: searchParams.get("TBK_TOKEN") || undefined,
    TBK_ORDEN_COMPRA: searchParams.get("TBK_ORDEN_COMPRA") || undefined,
    TBK_ID_SESION: searchParams.get("TBK_ID_SESION") || undefined,
  };
}

function hayParamsCommit(searchParams) {
  return !!(
    searchParams.get("token_ws") ||
    searchParams.get("TBK_TOKEN") ||
    searchParams.get("TBK_ORDEN_COMPRA")
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes de UI
// ---------------------------------------------------------------------------

function EstadoCargando({ mensaje }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={300}
      gridGap={16}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="textSecondary">
        {mensaje}
      </Typography>
    </Box>
  );
}

function EstadoError({ mensaje, onVolver }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={300}
      gridGap={16}
      style={{ textAlign: "center", padding: 24 }}
    >
      <ErrorOutlineIcon style={{ fontSize: 64, color: "#F44336" }} />
      <Typography variant="h6" color="error">
        Pago no procesado
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {mensaje}
      </Typography>
      <Button
        variant="outlined"
        color="primary"
        onClick={onVolver}
        style={{ marginTop: 16 }}
      >
        Volver a mis facturas
      </Button>
    </Box>
  );
}

function ComprobanteDialog({
  open,
  resultado,
  esModoMultiple,
  facturasMultiple,
  tipoPago,
  descargandoPDF,
  comprobante,
  comprobanteDisponible,
  onDescargarPDF,
  onCerrar,
}) {
  if (!resultado?.data) return null;

  const commit = resultado.data;

  const tipoTarjeta =
    MEDIO_PAGO[commit.payment_type_code] ?? commit.payment_type_code;
  const cuotas = describirCuotas(
    commit.payment_type_code,
    commit.installments_number,
  );
  const facturasComprobante = comprobante?.facturas ?? [];
  const facturaComprobante = facturasComprobante[0];
  const cuentaPagadora = formatearIdentidadComprobante(
    comprobante?.cuentaPagadora,
  );
  const comprobantePendiente = !comprobanteDisponible;

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" alignItems="center" gridGap={8}>
          <CheckCircleOutlineIcon
            style={{ color: "#4CAF50", marginRight: 8 }}
          />
          {comprobantePendiente ? "Pago autorizado" : "Comprobante de pago"}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <List disablePadding>
          {comprobantePendiente && (
            <ListItem divider>
              <ListItemText
                primary="Comprobante pendiente de registro"
                secondary="Transbank autorizó el pago, pero todavía no está disponible el comprobante. No vuelvas a pagar esta factura; revisa Mis facturas más tarde o contacta a soporte si el estado no se actualiza."
              />
            </ListItem>
          )}
          <ListItem divider>
            <ListItemText primary="Comercio" secondary="Taxtic" />
          </ListItem>
          <ListItem divider>
            <ListItemText
              primary="Orden de compra"
              secondary={commit.buy_order}
            />
          </ListItem>
          <ListItem divider>
            <ListItemText
              primary="Descripción"
              secondary={
                esModoMultiple
                  ? `Pago de ${facturasMultiple.length} facturas - Servicios contables Taxtic`
                  : `Pago de factura - ${
                      TIPO_LABEL[facturaComprobante?.tipoFactura ?? tipoPago] ??
                      "Servicio contable"
                    }`
              }
            />
          </ListItem>
          {cuentaPagadora && (
            <ListItem divider>
              <ListItemText
                primary="Cuenta que realizó el pago"
                secondary={cuentaPagadora}
              />
            </ListItem>
          )}
          <ListItem divider>
            <ListItemText
              primary="Fecha y hora"
              secondary={formatearFechaDisplay(commit.transaction_date, true)}
            />
          </ListItem>
          <ListItem divider>
            <ListItemText
              primary="Monto pagado"
              secondary={formatAsCLP(commit.amount)}
            />
          </ListItem>
          <ListItem divider>
            <ListItemText
              primary="Código de autorización"
              secondary={commit.authorization_code}
            />
          </ListItem>
          <ListItem divider>
            <ListItemText primary="Medio de pago" secondary={tipoTarjeta} />
          </ListItem>
          {commit?.installments_number > 0 && (
            <ListItem divider>
              <ListItemText primary="Cuotas" secondary={cuotas} />
            </ListItem>
          )}
          {commit.card_detail?.card_number && (
            <ListItem divider>
              <ListItemText
                primary="Tarjeta"
                secondary={`**** **** **** ${commit.card_detail.card_number}`}
              />
            </ListItem>
          )}
          {!esModoMultiple && facturaComprobante && (
            <ListItem divider>
              <ListItemText
                primary="Sociedad titular de la factura"
                secondary={formatearIdentidadComprobante({
                  nombre: facturaComprobante.nombreSociedad,
                  rut: facturaComprobante.rutSociedad,
                })}
              />
            </ListItem>
          )}
          {!esModoMultiple && facturaComprobante && (
            <ListItem>
              <ListItemText
                primary="Folio factura"
                secondary={facturaComprobante.folio}
              />
            </ListItem>
          )}
          {esModoMultiple && facturasComprobante.length > 0 && (
            <ListItem>
              <ListItemText
                primary="Facturas pagadas"
                secondary={
                  <Box component="span">
                    {facturasComprobante.map((item) => (
                      <Typography
                        key={`${item.tipoFactura}-${item.idFactura}`}
                        component="span"
                        variant="body2"
                        color="textSecondary"
                        display="block"
                      >
                        {formatearFacturaComprobante(item)}
                      </Typography>
                    ))}
                  </Box>
                }
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onDescargarPDF}
          disabled={descargandoPDF || !comprobanteDisponible}
          startIcon={
            descargandoPDF ? <CircularProgress size={16} /> : <GetAppIcon />
          }
        >
          {descargandoPDF ? "Generando..." : "Descargar PDF"}
        </Button>
        <Button onClick={onCerrar} color="primary" variant="contained">
          Ir a mis facturas
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function PagosWebPay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { rut } = useClienteActual();

  // ── Modo múltiple: pathname termina en /pago/multiple, datos en location.state
  const esModoMultiple = location.pathname.endsWith("/pago/multiple");
  // Al llegar con state (primera visita), persistir en sessionStorage
  // Al volver de Transbank (sin state), recuperar desde sessionStorage
  const facturasMultiple = useMemo(() => {
    if (location.state?.facturas) {
      sessionStorage.setItem(
        "webpay_multiple_facturas",
        JSON.stringify({
          facturas: location.state.facturas,
          montoTotal: location.state.montoTotal,
        }),
      );
      return location.state.facturas;
    }
    const guardado = sessionStorage.getItem("webpay_multiple_facturas");
    return guardado ? JSON.parse(guardado).facturas : null;
  }, [location.state]);

  const montoTotalMultiple = useMemo(() => {
    if (location.state?.montoTotal) return location.state.montoTotal;
    const guardado = sessionStorage.getItem("webpay_multiple_facturas");
    return guardado ? JSON.parse(guardado).montoTotal : 0;
  }, [location.state]);
  const tieneCommit = hayParamsCommit(searchParams);

  // Guard: si llegamos a /multiple sin state (ej. reload), redirigir
  useEffect(() => {
    if (esModoMultiple && !facturasMultiple && !tieneCommit) {
      navigate("/dashboard/mis-facturas", { replace: true });
    }
  }, [esModoMultiple, facturasMultiple, tieneCommit, navigate]);

  // parsearId solo se llama en modo individual
  const { idFactura, tipoPago, tipoQuery } = esModoMultiple
    ? { idFactura: null, tipoPago: null, tipoQuery: null }
    : parsearId(id);

  // Estado de la máquina
  const [fase, setFase] = useState(
    tieneCommit ? FASE.PROCESANDO : FASE.CARGANDO,
  );
  const [transaccion, setTransaccion] = useState(null); // { token, url }
  const [resultado, setResultado] = useState(null); // respuesta commit
  const [comprobante, setComprobante] = useState(null); // detalle autoritativo del backend
  const [errorMsg, setErrorMsg] = useState(null);

  // Ref para evitar llamadas duplicadas en StrictMode
  const creandoRef = useRef(false);
  const commitRef = useRef(false);
  const registrandoRef = useRef(false);

  const [descargandoPDF, setDescargandoPDF] = useState(false);

  const handleDescargarPDF = async () => {
    if (!resultado?.data) return;
    if (!esModoMultiple && !factura) return;
    if (!comprobante?.facturas?.length) return;
    setDescargandoPDF(true);
    try {
      const facturasComprobante = comprobante.facturas;
      const facturaComprobante = facturasComprobante[0];

      await generarYDescargarComprobantePDF({
        nombreCliente:
          comprobante.cuentaPagadora?.nombre ??
          comprobante.cuentaPagadora?.rut ??
          "Cliente",
        cuentaPagadora: comprobante.cuentaPagadora,
        ...(esModoMultiple
          ? {
              facturas: facturasComprobante,
              monto: montoTotalMultiple,
            }
          : {
              folio: facturaComprobante.folio,
              monto: resultado.data.amount,
              tipoPago: facturaComprobante.tipoFactura,
              descripcion: facturaComprobante.descripcion ?? null,
              nombreSociedad: facturaComprobante.nombreSociedad,
              rutSociedad: facturaComprobante.rutSociedad,
            }),
        datosTransbank: {
          token_ws: searchParams.get("token_ws"),
          commitResponse: resultado.data,
        },
      });
    } catch (err) {
      console.error("[WebPay] Error al generar PDF:", err);
    } finally {
      setDescargandoPDF(false);
    }
  };

  // ---------------------------------------------------------------------------
  // RTK Query
  // ---------------------------------------------------------------------------

  const { data: factura, isLoading: cargandoFactura } =
    useObtenerFacturaEspecificaQuery(
      { idFactura, tipoFactura: tipoQuery },
      { skip: esModoMultiple },
    );

  const [crearTransaccion] = useCrearTransaccionMutation();
  const [commitTransaccion] = useCommitTransaccionMutation();
  const [registrarPago] = useRegistrarPagoWebPayMutation();
  const [registrarPagoMultiple] = useRegistrarPagoMultipleMutation();

  // ---------------------------------------------------------------------------
  // Efecto A: Crear transacción en Transbank
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (tieneCommit || creandoRef.current) return;
    if (esModoMultiple && !facturasMultiple) return;
    if (!esModoMultiple && (cargandoFactura || !factura)) return;

    // Modo individual: verificar que la factura no esté ya pagada
    if (!esModoMultiple) {
      if (factura.estado === "PAGADO" || factura.estado === "ANULADO") {
        navigate("/dashboard/mis-facturas", { replace: true });
        return;
      }
    }

    // Reload antes del pago: restaurar transacción ya creada desde sessionStorage
    // para evitar duplicar registros en transaccionesWebPay y junction
    if (esModoMultiple) {
      const guardado = sessionStorage.getItem("webpay_multiple_facturas");
      if (guardado) {
        const parsed = JSON.parse(guardado);
        if (parsed.transaccion) {
          creandoRef.current = true;
          setTransaccion(parsed.transaccion);
          setFase(FASE.LISTO);
          return;
        }
      }
    }

    creandoRef.current = true;
    setFase(FASE.CREANDO);

    const returnUrl = window.location.origin + window.location.pathname;

    if (esModoMultiple) {
      crearTransaccion({
        tipoFactura: "MULTIPLE",
        amount: Number.parseFloat(montoTotalMultiple),
        returnUrl,
        facturas: facturasMultiple,
      })
        .unwrap()
        .then((data) => {
          const guardado = sessionStorage.getItem("webpay_multiple_facturas");
          if (guardado) {
            sessionStorage.setItem(
              "webpay_multiple_facturas",
              JSON.stringify({ ...JSON.parse(guardado), transaccion: data }),
            );
          }
          setTransaccion(data);
          setFase(FASE.LISTO);
        })
        .catch(() => {
          setErrorMsg(
            "No se pudo iniciar el proceso de pago. Intente nuevamente.",
          );
          setFase(FASE.ERROR);
        });
      return;
    }

    // Modo individual — usa montoRestante para facturas con abono previo
    const monto =
      factura.montoRestante ?? factura.valorPesos ?? factura.total ?? 0;

    crearTransaccion({
      idFactura: Number(idFactura),
      tipoFactura: tipoPago,
      amount: Number.parseFloat(monto),
      returnUrl,
    })
      .unwrap()
      .then((data) => {
        setTransaccion(data);
        setFase(FASE.LISTO);
      })
      .catch(() => {
        setErrorMsg(
          "No se pudo iniciar el proceso de pago. Intente nuevamente.",
        );
        setFase(FASE.ERROR);
      });
  }, [
    factura,
    cargandoFactura,
    tieneCommit,
    esModoMultiple,
    crearTransaccion,
    idFactura,
    tipoPago,
    montoTotalMultiple,
    navigate,
    facturasMultiple,
  ]);

  // ---------------------------------------------------------------------------
  // Efecto B: Procesar retorno de Transbank (commit) — igual para ambos modos
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!tieneCommit || commitRef.current) return;

    commitRef.current = true;

    const params = extraerParamsCommit(searchParams);

    commitTransaccion(params)
      .unwrap()
      .then((commitResult) => {
        if (commitResult.success) {
          setResultado(commitResult);
        } else {
          setErrorMsg(
            commitResult.message ||
              "El pago no fue autorizado por el banco. No se realizó ningún cargo.",
          );
          setFase(FASE.ERROR);
        }
      })
      .catch(() => {
        setErrorMsg(
          "Error al confirmar el pago con Transbank. Contacte a soporte.",
        );
        setFase(FASE.ERROR);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar

  // ---------------------------------------------------------------------------
  // Efecto C: Registrar pago en BD
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!resultado || registrandoRef.current) return;
    if (!esModoMultiple && !factura) return;

    registrandoRef.current = true;

    const commit = resultado.data;
    if (!commit) return;

    const token_ws = searchParams.get("token_ws");
    const datosTransbank = { token_ws, commitResponse: commit };

    if (esModoMultiple) {
      registrarPagoMultiple({
        facturas: facturasMultiple,
        montoTotal: montoTotalMultiple,
        datosTransbank,
      })
        .unwrap()
        .then((data) => {
          setComprobante(data?.comprobante ?? null);
        })
        .catch((err) => {
          console.error("[WebPay] Error al registrar pago múltiple:", err);
        })
        .finally(() => {
          setFase(FASE.EXITO);
        });
      return;
    }

    // Modo individual
    registrarPago({
      tipoFactura: tipoPago,
      idFactura: Number(idFactura),
      ...(tipoPago === "TAREA" && { idTarea: factura.refTarea }),
      monto: commit.amount,
      sucursal: factura.sucursal,
      datosTransbank,
    })
      .unwrap()
      .then((data) => {
        setComprobante(data?.comprobante ?? null);
      })
      .catch((err) => {
        console.error("[WebPay] Error al registrar pago:", err);
      })
      .finally(() => {
        sessionStorage.removeItem("webpay_multiple_facturas");
        setFase(FASE.EXITO);
      });
  }, [
    resultado,
    factura,
    esModoMultiple,
    idFactura,
    registrarPago,
    registrarPagoMultiple,
    tipoPago,
    searchParams,
    facturasMultiple,
    montoTotalMultiple,
  ]);

  // ---------------------------------------------------------------------------
  // Render: estados de carga
  // ---------------------------------------------------------------------------

  if (
    fase === FASE.CARGANDO ||
    fase === FASE.PROCESANDO ||
    fase === FASE.CREANDO
  ) {
    const mensajes = {
      [FASE.PROCESANDO]: "Confirmando tu pago con Transbank...",
      [FASE.CREANDO]: "Preparando el proceso de pago...",
      [FASE.CARGANDO]: "Cargando información de tu factura...",
    };
    return (
      <Container maxWidth="sm" style={{ paddingTop: 48 }}>
        <EstadoCargando mensaje={mensajes[fase]} />
      </Container>
    );
  }

  if (fase === FASE.ERROR) {
    return (
      <Container maxWidth="sm" style={{ paddingTop: 48 }}>
        <EstadoError
          mensaje={errorMsg}
          onVolver={() => navigate("/dashboard/mis-facturas")}
        />
      </Container>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: listo para pagar
  // ---------------------------------------------------------------------------

  return (
    <Container maxWidth="sm" style={{ paddingTop: 32 }}>
      {/* ── Modo MÚLTIPLE: card con listado de facturas ── */}
      {esModoMultiple && fase === FASE.LISTO && (
        <Card elevation={2}>
          <CardHeader
            title="Resumen de pago"
            subheader={`${facturasMultiple.length} facturas seleccionadas`}
          />
          <Divider />
          <CardContent>
            <Box
              component="img"
              src="/static/webpay.jpeg"
              alt="WebPay Plus"
              style={{
                width: "100%",
                maxHeight: 120,
                objectFit: "contain",
                marginBottom: 24,
              }}
            />
            <List disablePadding>
              {facturasMultiple.map((f, idx) => (
                <ListItem key={idx} divider>
                  <ListItemText
                    primary={`Folio ${f.folio} - ${f.tipoFactura === "PLAN" ? "Plan mensual" : "Tarea"}`}
                    secondary={formatAsCLP(f.monto)}
                  />
                </ListItem>
              ))}
              <ListItem>
                <ListItemText
                  primary="Total a pagar"
                  secondary={
                    <Typography variant="h5" color="primary" component="span">
                      {formatAsCLP(montoTotalMultiple)}
                    </Typography>
                  }
                />
              </ListItem>
            </List>

            {/* Formulario de redireccionamiento a Transbank */}
            {transaccion && (
              <Box marginTop={3}>
                <form method="POST" action={transaccion.url}>
                  <input
                    type="hidden"
                    name="token_ws"
                    value={transaccion.token}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                  >
                    Pagar con WebPay
                  </Button>
                </form>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Modo INDIVIDUAL: card con datos de una factura ── */}
      {!esModoMultiple && factura && fase === FASE.LISTO && (
        <Card elevation={2}>
          <CardHeader
            title="Resumen de pago"
            subheader={`Folio ${factura.folio} — ${TIPO_LABEL[tipoPago]}`}
          />
          <Divider />
          <CardContent>
            <Box
              component="img"
              src="/static/webpay.jpeg"
              alt="WebPay Plus"
              style={{
                width: "100%",
                maxHeight: 120,
                objectFit: "contain",
                marginBottom: 24,
              }}
            />
            <List disablePadding>
              <ListItem divider>
                <ListItemText
                  primary="Monto a pagar"
                  secondary={
                    <Typography variant="h5" color="primary" component="span">
                      {formatAsCLP(
                        factura.montoRestante ??
                          factura.valorPesos ??
                          factura.total,
                      )}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem divider>
                <ListItemText
                  primary="Período"
                  secondary={
                    factura.fechaFacturacion
                      ? formatearFechaDisplay(factura.fechaFacturacion)
                      : "—"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Cliente RUT" secondary={rut ?? "—"} />
              </ListItem>
            </List>

            {/* Formulario de redireccionamiento a Transbank */}
            {transaccion && (
              <Box marginTop={3}>
                <form method="POST" action={transaccion.url}>
                  <input
                    type="hidden"
                    name="token_ws"
                    value={transaccion.token}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                  >
                    Pagar con WebPay
                  </Button>
                </form>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Dialog comprobante de pago exitoso ── */}
      <ComprobanteDialog
        open={fase === FASE.EXITO}
        resultado={resultado}
        esModoMultiple={esModoMultiple}
        facturasMultiple={facturasMultiple}
        tipoPago={tipoPago}
        descargandoPDF={descargandoPDF}
        comprobante={comprobante}
        comprobanteDisponible={Boolean(comprobante?.facturas?.length)}
        onDescargarPDF={handleDescargarPDF}
        onCerrar={() => navigate("/dashboard/mis-facturas", { replace: true })}
      />
    </Container>
  );
}
